import { useEffect } from 'react';

/**
 * FRONT-2307: the browser tab shows when Eddy is waiting on you.
 *
 * Philipp's report: he stepped away while Eddy waited on an approval, and
 * nothing outside the Eddy window said so. The in-app activity pill already
 * blinks amber "N asking you" (`status-cluster.tsx`); this mirrors that SAME
 * number onto the tab — a `(N) ` title prefix and an amber dot on the Eddy
 * favicon — so a person on another tab or window can see it. The same count
 * on purpose (Marko, FRONT-2307 mockup review): pending approvals, open
 * questions and plans alike, so the tab and the pill can never disagree.
 *
 * Static dot, no blink: a favicon swap per frame is wasteful and the title
 * already carries the count.
 */

/** Tailwind v4's `amber-500` (oklch(76.9% 0.188 70.08)) in sRGB hex — the
 *  pill's "asking you" colour (`STATE_COLOR_CLASS.asking`), written out
 *  because a favicon is a standalone SVG with no access to the theme. */
export const ATTENTION_DOT = '#FE9A00';

/** `(N) <title>` while anything is waiting; the title untouched otherwise. */
export function withAttentionPrefix(title: string, count: number): string {
  return count > 0 ? `(${count}) ${title}` : title;
}

/** The Eddy E mark (`public/favicon.svg`) with an amber dot in its top-right corner. */
export function attentionFaviconSvg(): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="14" fill="#2962FF"/>` +
    `<rect x="13" y="8" width="38" height="12" rx="2.5" fill="#FFFFFF"/>` +
    `<rect x="13" y="26" width="24" height="12" rx="2.5" fill="#FFFFFF" opacity="0.62"/>` +
    `<rect x="13" y="44" width="38" height="12" rx="2.5" fill="#FFFFFF"/>` +
    `<circle cx="50" cy="14" r="13" fill="${ATTENTION_DOT}" stroke="#FFFFFF" stroke-width="4"/>` +
    `</svg>`
  );
}

export const ATTENTION_FAVICON_URI = `data:image/svg+xml,${encodeURIComponent(attentionFaviconSvg())}`;

/**
 * Swaps every `<link rel="icon">` to the dotted mark while `active`, and puts
 * the originals back the moment it isn't (or on unmount) — the same swap as
 * the onboarding progress favicon (`use-eddy-favicon-progress.ts`), kept
 * separate because that one re-renders a fill fraction and only runs on
 * /onboard, outside the app shell this one lives in.
 */
export function useAttentionFavicon(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
    if (links.length === 0) return;
    const originals = links.map((l) => l.href);
    for (const l of links) l.href = ATTENTION_FAVICON_URI;
    return () => {
      links.forEach((l, i) => {
        l.href = originals[i];
      });
    };
  }, [active]);
}
